import React, { type HTMLAttributes } from 'react';
import tabbable from 'tabbable';
import cx from 'classnames';
import { mcdsFlagCheck } from '@mc/wink/helpers/utils-ts';
import stylesheet from './Menu.css';

export type MenuProps = {
  /** Consumes `MenuItem` components */
  children?: React.ReactNode;
  /** Current state on whether Menu is expanded or collapsed */
  isExpanded?: boolean;
  /** Function that updates isExpanded value */
  setIsExpanded?: (expanded: boolean) => void;
  /** Determines whether the menu has a max-height*/
  fullHeight?: boolean;
  /** Determines the size of the menu */
  size?: 'small' | 'medium' | 'large';
} & HTMLAttributes<HTMLDivElement>;

const Menu = React.forwardRef<HTMLDivElement, MenuProps>(function Menu(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  { children, setIsExpanded = () => {}, fullHeight, size = 'small', ...props },
  menuRef,
) {
  const isReleased = mcdsFlagCheck(
    'xp_mcds_redesign_components_organisms_part_2',
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    const { target, key, shiftKey } = e;
    const tabbables = (menuRef as $TSFixMe).current
      ? tabbable((menuRef as $TSFixMe).current)
      : [];
    const firstFocusableEl = tabbables[0];
    const lastFocusableEl = tabbables[tabbables.length - 1];
    switch (key) {
      case ' ': {
        if (
          target instanceof HTMLElement &&
          (target.tagName === 'A' || target.tagName === 'BUTTON')
        ) {
          /** This will prevent scrolling on the page when a link is focused. */
          e.preventDefault();
        }
        break;
      }
      case 'ArrowDown': {
        e.preventDefault();
        /** Focus to first element after it reaches last item */
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
        } else {
          const index = tabbables.indexOf(
            document.activeElement as HTMLElement,
          );
          if (tabbables[index + 1]) {
            tabbables[index + 1].focus();
          }
        }
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        /** Focus to last element after it reaches first item */
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
        } else {
          const index = tabbables.indexOf(
            document.activeElement as HTMLElement,
          );
          if (index === -1) {
            // In case focus gets lost or user clicks a disabled item
            if (lastFocusableEl) {
              lastFocusableEl.focus();
            }
          } else {
            tabbables[index - 1].focus();
          }
        }
        break;
      }
      case 'Tab': {
        if (!shiftKey) {
          /** When tabbing, focus to first element after it reaches last item */
          if (document.activeElement === lastFocusableEl) {
            firstFocusableEl.focus();
            e.preventDefault();
          }
        } else {
          /** Focus to last element after it reaches first item when pressing shift+key*/
          if (document.activeElement === firstFocusableEl) {
            lastFocusableEl.focus();
            e.preventDefault();
          }
        }
        break;
      }
      case 'Escape': {
        /** Close popover on ESC*/
        e.preventDefault();
        setIsExpanded(false);
        break;
      }
      default:
        break;
    }
  };

  return (
    isReleased && (
      <div
        className={cx(
          stylesheet.menu,
          fullHeight && stylesheet.fullHeight,
          size && stylesheet[size],
        )}
        onKeyDown={handleKeyDown}
        ref={menuRef}
        {...props}
      >
        {children}
      </div>
    )
  );
});

export { Menu as default };
